import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Grid,
  Fade,
  CircularProgress,
  useMediaQuery,
  Avatar,
  Slide
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  User,
  WalletMinimal,
  Percent,
  Ruler,
  Send,
  Award,
  TrendingUp
} from "lucide-react";
import ResponsiveAppBar from "../components/AppBar";
import Footer from "../components/Footer";
import { API_BASE_URL } from "../api/config";

// Custom styled components
const GoldGradientText = ({ children, ...props }) => (
  <Typography
  {...props}
  sx={{
    background: "linear-gradient(90deg, #936097 0%, #77c8c7 50%, #ffffff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textShadow: "0px 2px 4px rgba(0,0,0,0.1)",
    fontWeight: "bold",
    ...props.sx
  }}
>
  {children}
</Typography>
);

const GlowingButton = ({ children, ...props }) => (
<Button
    {...props}
    sx={{
      background: "linear-gradient(45deg, #936097 0%, #77c8c7 100%)",
      color: "#fff",
      fontWeight: "bold",
      padding: "12px 24px",
      borderRadius: "30px",
      boxShadow: "0 4px 10px rgba(147, 96, 151, 0.3)",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        boxShadow: "0 6px 18px rgba(119, 200, 199, 0.5)",
        transform: "translateY(-3px)",
      },
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-100%",
        width: "100%",
        height: "100%",
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
        transition: "0.5s",
      },
      "&:hover:before": {
        left: "100%",
      },
      ...props.sx
    }}
  >
    {children}
  </Button>
);

const AnimatedInfoCard = ({ icon, title, value, delay }) => {
  return (
    <Fade in timeout={500} style={{ transitionDelay: `${delay}ms` }}>
      <Paper
        elevation={4}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          borderRadius: 2,
          background: "linear-gradient(145deg, #fdfdfe 0%, #f3f6f9 100%)",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: "#93609722",
            color: "#77c8c7",
            mr: 2,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="#936097">
            {value}
          </Typography>
        </Box>
      </Paper>
    </Fade>
  );
};


const GoldLoanForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    borrowerName: "",
    email: "",
    phoneNumber: "",
    address: "",
    goldWeight: "",
  });
  const [goldPricePerGram, setGoldPricePerGram] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [calculatedGoldPrice, setCalculatedGoldPrice] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [typingText, setTypingText] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  // Fetch admin settings for gold price and interest rate
  useEffect(() => {
    const fetchAdminSettings = async () => {
      setLoadingSettings(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/admin/fetch_settings`);
        setGoldPricePerGram(response.data.goldPricePerGram);
        setInterestRate(response.data.interestRate);
      } catch (error) {
        console.error("Error fetching admin settings:", error);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchAdminSettings();
  }, []);

  // Calculate the gold price based on weight
  useEffect(() => {
    if (formData.goldWeight && goldPricePerGram) {
      setCalculatedGoldPrice(parseFloat(formData.goldWeight) * goldPricePerGram);
    } else {
      setCalculatedGoldPrice(0);
    }
  }, [formData.goldWeight, goldPricePerGram]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "goldWeight" || name === "loanAmount" || name === "loanTerm"
          ? parseFloat(value) || ""
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const loanData = {
      ...formData,
      interestRate,
      calculatedGoldPrice,
      goldPricePerGram,
    };

    try {
      await axios.post(`${API_BASE_URL}/gold-loan/add_goldLoan`, loanData);
      setSuccessMessage("✅ Your gold loan application has been successfully submitted!");
      setOpenSnackbar(true);
      setShowConfetti(true);

      // Reset form
      setFormData({
        borrowerName: "",
        email: "",
        phoneNumber: "",
        address: "",
        goldWeight: "",
      });
      setCalculatedGoldPrice(0);
      setCurrentStep(0);

      // Initiate typewriter effect after a delay
      setTimeout(() => {
        const message = "💰 Your Trusted Partner in Financial Growth 💰";
        let i = 0;
        let newText = "";
        const interval = setInterval(() => {
          newText += message[i];
          setTypingText(newText);
          i++;
          if (i >= message.length) clearInterval(interval);
        }, 100);
      }, 1000);

      // Hide confetti after 5 seconds
      setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSuccessMessage("❌ Failed to submit loan application. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Animation variants for form fields
  const formFieldVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      },
    }),
  };

  return (
    <>
      <ResponsiveAppBar />

      {/* CSS for animations */}
      <style>
        {`
    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }
    .cursor {
      display: inline-block;
      margin-left: 2px;
      animation: blink 1s step-end infinite;
    }

    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }

    .float-animation {
      animation: float 3s ease-in-out infinite;
    }

    @keyframes shine {
      0% { background-position: -100px; }
      100% { background-position: 200px; }
    }

    .shiny-text {
      position: relative;
      background: linear-gradient(90deg, #936097, #77c8c7, #ffffff);
      background-size: 200% auto;
      color: transparent;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: shine 3s linear infinite;
    }

    @keyframes confetti {
      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
      100% { transform: translateY(1000px) rotate(720deg); opacity: 0; }
    }

    .confetti {
      position: fixed;
      width: 10px;
      height: 10px;
      background-color: #936097;
      top: -10px;
      z-index: 9999;
    }

    .form-container {
      position: relative;
      z-index: 1;
    }

    .form-container::before {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      background: linear-gradient(45deg, #936097, #77c8c7, #ffffff, #936097);
      background-size: 400% 400%;
      z-index: -1;
      filter: blur(20px);
      opacity: 0.2;
      animation: gradientBG 15s ease infinite;
      border-radius: 15px;
    }

    @keyframes gradientBG {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .gold-bg-subtle {
      background: linear-gradient(135deg, rgba(147,96,151,0.05) 0%, rgba(119,200,199,0.1) 100%);
    }
  `}
      </style>


      {showConfetti && (
        <>
          {[...Array(50)].map((_, i) => {
            const randomLeft = Math.random() * 100;
            const randomDelay = Math.random() * 5;
            const randomSize = Math.random() * 10 + 5;
            const randomColor = [
              "#FFD700", // Gold
              "#FFA500", // Orange
              "#FFFFFF", // White
              "#E6C200", // Dark Gold
              "#FFE55C", // Light Gold
            ][Math.floor(Math.random() * 5)];

            return (
              <div
                key={i}
                className="confetti"
                style={{
                  left: `${randomLeft}%`,
                  width: `${randomSize}px`,
                  height: `${randomSize}px`,
                  backgroundColor: randomColor,
                  animationDuration: `${Math.random() * 3 + 2}s`,
                  animationDelay: `${randomDelay}s`,
                  animation: `confetti ${Math.random() * 3 + 2}s linear ${randomDelay}s forwards`,
                }}
              />
            );
          })}
        </>
      )}

      <Container maxWidth="md" sx={{ mt: 5, mb: 8 }}>
        <Grid container spacing={4}>
          {/* Gold Loan Information Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                textAlign: 'center',
                mb: 4,
                position: 'relative',
              }}
            >
              <div className="float-animation">
                <GoldGradientText variant={isMobile ? "h4" : "h3"} gutterBottom>
                  Gold Loan Application
                </GoldGradientText>
              </div>
              <Fade in timeout={1000}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{
                    maxWidth: '600px',
                    mx: 'auto',
                    mb: 3,
                    fontStyle: 'italic'
                  }}
                >
                  Unlock the value of your gold with our competitive rates and hassle-free process
                </Typography>
              </Fade>
            </Box>
          </Grid>

          {/* Info Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <AnimatedInfoCard
                  icon={<Award size={24} />}
                  title="Today's Gold Rate"
                  value={`Rs.${goldPricePerGram.toLocaleString('en-IN')} per gram`}
                  delay={200}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <AnimatedInfoCard
                  icon={<Percent size={24} />}
                  title="Interest Rate"
                  value={`${interestRate}% per annum`}
                  delay={400}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <AnimatedInfoCard
                  icon={<TrendingUp size={24} />}
                  title="Max Loan Value"
                  value="Up to 75% of Gold Value"
                  delay={600}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12}>
            <Box className="form-container">
              <Paper
                elevation={10}
                sx={{
                  p: isMobile ? 3 : 4,
                  borderRadius: 3,
                  background: "linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Abstract gold shapes in background */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -100,
                    right: -100,
                    width: 300,
                    height: 300,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(255,215,0,0.05) 0%, rgba(255,165,0,0.02) 70%, transparent 100%)",
                    zIndex: 0,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -80,
                    left: -80,
                    width: 200,
                    height: 200,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(255,215,0,0.05) 0%, rgba(255,165,0,0.02) 70%, transparent 100%)",
                    zIndex: 0,
                  }}
                />

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {currentStep === 0 && (
                    <Fade in timeout={500}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "#333",
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <User size={20} style={{ marginRight: '8px' }} />
                          Personal Information
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Full Name"
                              name="borrowerName"
                              fullWidth
                              value={formData.borrowerName}
                              onChange={handleChange}
                              required
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: 2,
                                  "&:hover": { boxShadow: "0 2px 5px rgba(0,0,0,0.1)" },
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 8px rgba(23, 218, 192, 0.4)",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                  },
                                },
                              }}
                              inputProps={{
                                sx: { py: 1.5 }
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Email"
                              name="email"
                              fullWidth
                              value={formData.email}
                              onChange={handleChange}
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: 2,
                                  "&:hover": { boxShadow: "0 2px 5px rgba(0,0,0,0.1)" },
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 8px rgba(23, 218, 192, 0.4)",

                                  },
                                },
                              }}
                              inputProps={{
                                sx: { py: 1.5 }
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Phone Number"
                              type="number"
                              name="phoneNumber"
                              fullWidth
                              value={formData.phoneNumber}
                              onChange={handleChange}
                              required
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: 2,
                                  "&:hover": { boxShadow: "0 2px 5px rgba(0,0,0,0.1)" },
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 8px rgba(23, 218, 192, 0.4)",

                                  },
                                },
                              }}
                              inputProps={{
                                sx: { py: 1.5 }
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Complete Address"
                              name="address"
                              fullWidth
                              multiline
                              rows={2}
                              value={formData.address}
                              onChange={handleChange}
                              required
                              variant="outlined"
                              InputProps={{
                                sx: {
                                  borderRadius: 2,
                                  "&:hover": { boxShadow: "0 2px 5px rgba(0,0,0,0.1)" },
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 8px rgba(23, 218, 192, 0.4)",

                                  },
                                },
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                          <GlowingButton
                            onClick={() => setCurrentStep(1)}
                            endIcon={<Send size={18} />}
                          >
                            Next Step
                          </GlowingButton>
                        </Box>
                      </Box>
                    </Fade>
                  )}

                  {currentStep === 1 && (
                    <Fade in timeout={500}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "#333",
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <WalletMinimal size={20} style={{ marginRight: '8px' }} />
                          Gold & Loan Details
                        </Typography>

                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <TextField
                              label="Gold Weight (grams)"
                              type="number"
                              name="goldWeight"
                              fullWidth
                              value={formData.goldWeight}
                              onChange={handleChange}
                              required
                              variant="outlined"
                              InputProps={{
                                startAdornment: (
                                  <Box sx={{ mr: 1, color: "#FFA500" }}>
                                    <Ruler size={20} />
                                  </Box>
                                ),
                                sx: {
                                  borderRadius: 2,
                                  "&:hover": { boxShadow: "0 2px 5px rgba(0,0,0,0.1)" },
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 8px rgba(23, 218, 192, 0.4)",

                                  },
                                },
                              }}
                              inputProps={{
                                sx: { py: 1.5 }
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Paper
                          elevation={3}
                          sx={{
                            mt: 3,
                            mb: 3,
                            p: 3,
                            borderRadius: 2,
                            background: "linear-gradient(to right, rgba(255,215,0,0.05), rgba(255,165,0,0.1))",
                            border: "1px solid rgba(198, 196, 187, 0.2)"
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Loan Calculation
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">
                                Gold Price Per Gram
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="#FFA500">
                                Rs.{goldPricePerGram.toLocaleString('en-IN')}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">
                                Total Gold Value
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="#FFA500">
                                Rs.{calculatedGoldPrice.toLocaleString('en-IN')}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Typography variant="body2" color="text.secondary">
                                Interest Rate (Annual)
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="#FFA500">
                                {interestRate}%
                              </Typography>
                            </Grid>
                          </Grid>

                          {calculatedGoldPrice > 0 && (
                            <Fade in timeout={500}>
                              <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed rgba(143, 198, 171, 0.3)" }}>
                                <Typography variant="body2" fontWeight="medium">
                                  Maximum Eligible Loan (75% of gold value):
                                </Typography>
                                <Typography variant="h5" className="shiny-text" fontWeight="bold">
                                  Rs.{(calculatedGoldPrice * 0.75).toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                            </Fade>
                          )}
                        </Paper>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setCurrentStep(0)}
                            sx={{
                              borderRadius: "30px",
                              borderColor: "#FFA500",
                              color: "#FFA500",
                              "&:hover": {
                                borderColor: "#FFD700",
                                backgroundColor: "rgba(255,215,0,0.05)",
                              }
                            }}
                          >
                            Back
                          </Button>

                          <GlowingButton
                            type="submit"
                            disabled={loading || !formData.goldWeight || !formData.borrowerName || !formData.phoneNumber || !formData.address}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                          >
                            {loading ? "Processing..." : "Submit Application"}
                          </GlowingButton>
                        </Box>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* Trust Indicators */}
          <Grid item xs={12}>
            <Fade in timeout={1000} style={{ transitionDelay: '500ms' }}>
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                {typingText && (
                  <Typography
                    variant="h5"
                    className="shiny-text"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    {typingText}
                    <span className="cursor">|</span>
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  We offer best-in-class security, transparent processing, and quick loan disbursals.
                </Typography>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Footer />
    </>
  );
};

export default GoldLoanForm;